export interface AnalyticsPayload {
  [key: string]: string | number | boolean | null | undefined;
}

export type AnalyticsDebugListener = (name: string, payload?: AnalyticsPayload) => void;

export type ClientErrorClass = 'window_error' | 'unhandled_rejection' | 'webgl_context_lost';

export const CLIENT_ERROR_MESSAGE_LIMIT = 120;

const debugListeners = new Set<AnalyticsDebugListener>();

export type AnalyticsPointer = 'coarse' | 'fine';
export type AnalyticsViewport = 'w560' | 'w800' | 'w1024' | 'wide';
export type AnalyticsOrientation = 'portrait' | 'landscape';

export interface AnalyticsDimensions {
  pointer: AnalyticsPointer;
  viewport: AnalyticsViewport;
  orientation: AnalyticsOrientation;
}

function mediaMatches(query: string): boolean {
  const matchMedia = (globalThis as { matchMedia?: (q: string) => { matches?: boolean } }).matchMedia;
  if (typeof matchMedia !== 'function') {
    return false;
  }
  try {
    return matchMedia(query).matches === true;
  } catch {
    return false;
  }
}

function viewportWidth(): number {
  const width = (globalThis as { innerWidth?: unknown }).innerWidth;
  return typeof width === 'number' && Number.isFinite(width) ? width : Number.POSITIVE_INFINITY;
}

/** Device fields attached to every Analytics.track call. */
export function readAnalyticsDimensions(): AnalyticsDimensions {
  const width = viewportWidth();
  let viewport: AnalyticsViewport = 'wide';
  if (width <= 560) {
    viewport = 'w560';
  } else if (width <= 800) {
    viewport = 'w800';
  } else if (width <= 1024) {
    viewport = 'w1024';
  }
  return {
    pointer: mediaMatches('(pointer: coarse)') ? 'coarse' : 'fine',
    viewport,
    orientation: mediaMatches('(orientation: portrait)') ? 'portrait' : 'landscape',
  };
}

/**
 * Bounded client-error text. Strings and Error.message only.
 * Objects are dropped so a rejected save cannot be serialized.
 */
export function boundClientErrorMessage(value: unknown): string {
  let text = '';
  if (typeof value === 'string') {
    text = value;
  } else if (value instanceof Error) {
    text = value.message;
  }
  return text.slice(0, CLIENT_ERROR_MESSAGE_LIMIT);
}

export function trackClientError(errorClass: ClientErrorClass, value: unknown): void {
  Analytics.track('client_error', {
    class: errorClass,
    message: boundClientErrorMessage(value),
  });
}

export const Analytics = {
  track(name: string, payload?: AnalyticsPayload): void {
    const enriched: AnalyticsPayload = {
      ...payload,
      ...readAnalyticsDimensions(),
    };
    if (import.meta.env.DEV) {
      console.info('[analytics]', name, enriched);
    }
    for (const listener of debugListeners) {
      listener(name, enriched);
    }
  },
  /** Test/debug only. Not a vendor, backend, or dashboard. */
  addDebugListener(listener: AnalyticsDebugListener): () => void {
    debugListeners.add(listener);
    return () => {
      debugListeners.delete(listener);
    };
  },
};
